export interface ClassificationDisplayInfo {
  label: string;
  color: string;
  description?: string;
}

/**
 * Maps email classification results to user-readable labels and styling
 */
export function getClassificationDisplayInfo(
  classificationResult: string | null | undefined,
): ClassificationDisplayInfo {
  // Convert to lowercase for consistent matching, defaulting to 'unknown' for null/undefined
  const result = (classificationResult ?? 'unknown').toString().toLowerCase();

  switch (result) {
    case 'purchase_order':
      return {
        label: 'Purchase Order',
        color: 'success.main',
        description: 'Email contains a purchase order from a customer',
      };
    case 'purchase_order_update':
      return {
        label: 'Purchase Order Update',
        color: 'info.main',
        description: 'Email contains updates to an existing purchase order',
      };
    case 'purchase_order_notification':
      return {
        label: 'Purchase Order Notification',
        color: 'info.main',
        description: 'Email contains notifications about a purchase order',
      };
    case 'sales_order_draft_thread_reply':
      return {
        label: 'Thread Reply',
        color: 'secondary.main',
        description: 'Email is a reply in an existing sales order thread',
      };
    case 'procurement_pricing_quote_sheet':
      return {
        label: 'Quote Sheet',
        color: 'primary.main',
        description: 'Email contains pricing or quote information',
      };
    case 'ap_invoice':
      return {
        label: 'Invoice (AP)',
        color: 'info.main',
        description: 'Email contains an invoice (AP)',
      };
    case 'bill_of_lading':
      return {
        label: 'Bill of Lading',
        color: 'info.main',
        description: 'Shipment manifest issued by the carrier',
      };
    case 'proof_of_delivery':
      return {
        label: 'Proof of Delivery',
        color: 'info.main',
        description: 'Receipt confirming the shipment was delivered',
      };
    case 'receiving_stamp':
      return {
        label: 'Receiving Stamp',
        color: 'info.main',
        description: 'Stamp / annotation recording goods received',
      };
    case 'carrier_confirmation':
      return {
        label: 'Carrier Confirmation',
        color: 'info.main',
        description:
          'Rate confirmation from a carrier or broker for a freight move',
      };
    case 'unknown':
      return {
        label: 'Unknown',
        color: 'warning.main',
        description: 'Email classification could not be determined',
      };
    default:
      return {
        label: formatClassificationLabel(result),
        color: 'text.secondary',
        description: 'Custom or unrecognized classification',
      };
  }
}

/**
 * Formats a raw classification string into a readable label
 */
function formatClassificationLabel(raw: string): string {
  return raw.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

/**
 * Gets the appropriate color for a classification result
 */
export function getClassificationColor(
  classificationResult: string | null | undefined,
): string {
  return getClassificationDisplayInfo(classificationResult).color;
}

/**
 * Gets the user-readable label for a classification result
 */
export function getClassificationLabel(
  classificationResult: string | null | undefined,
): string {
  return getClassificationDisplayInfo(classificationResult).label;
}
